/**
 * Asset registry: single source of truth for currency metadata.
 * Supports multi-currency architecture (currently USDC only).
 */

export interface AssetConfig {
  symbol: string;
  contractAddress: `0x${string}` | null; // null = native currency (ETH)
  decimals: number;
  coingeckoId: string;
  guardarianTicker: string;
  guardarianNetwork: string;
  network: string;
  displayDecimals: number;
  enabled: boolean;
}

const USDC_ADDRESS = (import.meta.env.VITE_USDC_ADDRESS as string | undefined)?.trim()
  || '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';

const USDC: AssetConfig = {
  symbol: 'USDC',
  contractAddress: USDC_ADDRESS as `0x${string}`,
  decimals: 6,
  coingeckoId: 'usd-coin',
  guardarianTicker: 'USDC',
  guardarianNetwork: 'BASE',
  network: 'BASE_MAINNET',
  displayDecimals: 2,
  enabled: true,
};

export const ASSETS: Record<string, AssetConfig> = {
  USDC,
};

export const DEFAULT_ASSET: AssetConfig = USDC;

/**
 * Get an asset by symbol. Returns DEFAULT_ASSET if not found (safe fallback).
 */
export function getAsset(symbol: string): AssetConfig {
  return ASSETS[symbol] || DEFAULT_ASSET;
}

/**
 * Format an amount for display according to the asset's displayDecimals.
 */
export function formatAssetAmount(amount: string | number, asset: AssetConfig = DEFAULT_ASSET): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0';
  return num.toFixed(asset.displayDecimals);
}
